import { useSelector } from 'react-redux';
import { datasetUrl } from '@/constants/urls';
import { readStates, saveStates } from '@/constants/lifecycle';
import { resolveCatalogueTemplate, sectionsWithFields } from '../helper';
import { datasetDirtyFlags, datasetSourceAndStepAttributes } from '../../state/state';
import { datasetPerms } from '../../sharing/state/state';
import { datasetSettingsFields } from '../../settings/state/state';
import canManageDataset from '../../sharing/helper';
import { datasetIsEditing, isNewDataset } from '../../helper';
import { datasetPermissions } from '../../sharing/constants';
import { cataloguePanels } from '../templates/state/state';
import { dataModelName, isVisibleDataModel } from '../../datamodels/helper';

const useCatalogueTitle = ({ category, code }) => useSelector(({
  auth: { role: { permissions: { datasets: { update } } } },
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template, editable } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    catalogue: { title: '' },
    ...readStates,
  });
  const dataModel = isVisibleDataModel(dataset.data_model) ? dataModelName(dataset.data_model) : null;

  return {
    canEdit: update && dataset.permissions[datasetPermissions.UPDATE],
    dataModel,
    disabled: !editable,
    isEditing: datasetIsEditing(dataset),
    name: dataset.catalogue.title,
    template,
  };
});

const useCatalogueTitleInput = ({ category, code }) => useSelector(({
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    catalogue: { title: '' },
    ...saveStates,
  });

  return {
    name: dataset.catalogue.title,
    saveErrors: dataset.saveErrors,
    template,
  };
});

const useCataloguePanel = ({ category, code }) => useSelector(({
  datasets: { datasets },
  catalogues: { templates },
  auth: { currentUser },
}) => {
  const { dataset } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    code,
    permissions: datasetPerms(),
    settings: datasetSettingsFields(),
  });

  return {
    allowManage: canManageDataset(dataset.permissions),
    canSubscribe: !currentUser.isGuest && dataset.settings.allow_dataset_update_subscriptions,
    code: dataset.code,
    url: datasetUrl(dataset),
    visibility: dataset.settings.visibility,
  };
});

const useCataloguePanelContainer = ({ category, code }) => useSelector(({
  auth: { role: { permissions } },
  datasets: { datasets },
  catalogues: { templates },
}) => {
  const { dataset, template, exists } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    catalogue: { title: '' },
    isDirty: datasetDirtyFlags(),
    permissions: datasetPerms(),
    ...readStates,
    ...saveStates,
  });

  return {
    canRead: exists && permissions.datasets.read && permissions.cataloguedefinitions.read,
    exists,
    isDirty: dataset.isDirty,
    name: dataset.catalogue.title,
    readErrors: dataset.readErrors.concat(template.readErrors),
    readStatus: dataset.readStatus,
    saveErrors: dataset.saveErrors,
    templateReadStatus: template.readStatus,
    url: datasetUrl(dataset),
  };
});

const basicDatasetTemplateState = (dataset, template) => ({
  readStatus: dataset.readStatus,
  sections: template.sections,
  templateCode: dataset.settings.catalogue_definition_code,
  templateReadStatus: template.readStatus,
});

const useDatasetCatalogue = ({ category, code }) => useSelector(({
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    ...readStates,
    panels: cataloguePanels().panels,
    settings: datasetSettingsFields(),
  });

  return { ...basicDatasetTemplateState(dataset, template), panels: dataset.panels };
});

const useDatasetCatalogueSection = ({ category, code }) => useSelector(({
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    ...readStates,
    ...saveStates,
    settings: datasetSettingsFields(),
    steps: datasetSourceAndStepAttributes(),
  });

  return {
    ...basicDatasetTemplateState(dataset, template),
    activeStepIndex: dataset.steps.activeStepIndex,
    saveErrors: dataset.saveErrors,
    sectionsWithFields: sectionsWithFields(template),
  };
});

const useDatasetCatalogueAccordionFieldList = ({
  category, code, section, panel, index, url,
}) => useSelector(({
  catalogues: { templates },
  datasets: { datasets },
}) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, { category, code }, {
    ...cataloguePanels(),
  });
  const { isNew } = isNewDataset(dataset, url);

  return {
    active: dataset.panels[panel].includes(index),
    hasChildren: !isNew && (['first', 'third'].includes(section) || (section === 'second' && !template.sections.third)),
    label: template.sections[section].label,
  };
});

export default {
  useCataloguePanel,
  useCataloguePanelContainer,
  useCatalogueTitle,
  useCatalogueTitleInput,
  useDatasetCatalogue,
  useDatasetCatalogueSection,
  useDatasetCatalogueAccordionFieldList,
};
